import { DestroyRef, Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import {
  tap,
  catchError,
  map,
  switchMap,
  distinctUntilChanged,
} from 'rxjs/operators';
import { IAuthService } from '../interfaces/auth-service.interface';
import { AuthRepository } from '../../infrastructure/http/repositories/auth.repository';
import { User } from '../models/user.model';
import { Permissions } from '../enums';
import { FeatureFlagsFacade } from '../../store/feature-flags/feature-flags.facade';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({ providedIn: 'root' })
export class AuthService implements IAuthService {
  private readonly authRepository = inject(AuthRepository);
  private readonly featureFlagsFacade = inject(FeatureFlagsFacade);

  private readonly currentUserSubject = new BehaviorSubject<User | null>(null);
  public readonly currentUser$ = this.currentUserSubject.asObservable();

  // Authentication error state for displaying validation errors
  private readonly authErrorSubject = new BehaviorSubject<string | null>(null);
  public readonly authError$ = this.authErrorSubject.asObservable();

  private readonly permissionsSubject = new BehaviorSubject<string[]>([]);
  public readonly permissions$ = this.permissionsSubject.asObservable();
  private readonly destroyRef = inject(DestroyRef);
  private readonly diagnosticPageAccessSubject = new BehaviorSubject<boolean>(
    false
  );
  readonly diagnosticPageAccess$ =
    this.diagnosticPageAccessSubject.asObservable();

  // Cache for session validation to prevent repeated BFF calls
  private sessionValidationInProgress = false;

  constructor() {
    this.permissions$
      .pipe(
        switchMap(permissions =>
          permissions.includes(Permissions.AccessDiagnosticPage)
            ? this.featureFlagsFacade.enableDiagnosticPageAccess$
            : of(false)
        ),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(enabled => {
        this.diagnosticPageAccessSubject.next(enabled);
      });
  }

  /**
   * Redirect to BFF login page.
   * BFF will handle OAuth flow and redirect back to frontend.
   */
  loginWithSSO(): void {
    this.authRepository.redirectToBFFLogin();
    // No Observable return - page will redirect
  }

  /**
   * Logout via BFF.
   * Clears session cookie on BFF side and local user state.
   */
  logout(): Observable<void> {
    return this.authRepository.logoutBFF().pipe(
      tap({
        next: () => {
          this.currentUserSubject.next(null);
          this.permissionsSubject.next([]);
        },
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        error: _error => {
          // Even if BFF call fails, clear local state
          this.currentUserSubject.next(null);
          this.permissionsSubject.next([]);
        },
      }),
      catchError(() => of(void 0)) // Swallow errors, logout should always succeed locally
    );
  }

  /**
   * Validate BFF session and restore user state.
   * Called on app initialization and by auth guard.
   */
  validateSession(): Observable<User | null> {
    // Prevent concurrent validation calls
    if (this.sessionValidationInProgress) {
      return of(null);
    }

    this.sessionValidationInProgress = true;

    return this.authRepository.checkAuthStatus().pipe(
      map(response => {
        const permissions = response.permissions ?? [];
        // Check the isAuthenticated flag from BFF
        if (
          response.isAuthenticated &&
          response.user &&
          permissions.length > 0
        ) {
          this.setCurrentUser(response.user);
          this.permissionsSubject.next(permissions);
          this.sessionValidationInProgress = false;
          return response.user;
        } else {
          // BFF says not authenticated (even though it returned 200 OK)
          this.currentUserSubject.next(null);
          this.permissionsSubject.next([]);
          this.sessionValidationInProgress = false;
          return null;
        }
      }),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      catchError(_error => {
        this.currentUserSubject.next(null);
        this.permissionsSubject.next([]);
        this.sessionValidationInProgress = false;
        return of(null);
      })
    );
  }

  getPermissions(): Observable<string[]> {
    return this.permissions$;
  }

  hasPermission(permissions: string[]): Observable<boolean> {
    return this.permissions$.pipe(
      map(perms => permissions.some(permission => perms.includes(permission)))
    );
  }

  hasPermissionSync(permissions: string[]): boolean {
    return permissions.some(permission =>
      this.permissionsSubject.value.includes(permission)
    );
  }

  hasDiagnosticPagePermission(): boolean {
    return (
      this.hasPermissionSync([Permissions.AccessDiagnosticPage]) &&
      this.diagnosticPageAccessSubject.value
    );
  }

  getCurrentUser(): Observable<User | null> {
    return this.currentUser$;
  }

  isAuthenticated(): Observable<boolean> {
    return this.currentUser$.pipe(map(user => user !== null));
  }

  setCurrentUser(user: User | null): void {
    if (user?.name && (!user.firstName || !user.lastName)) {
      const nameParts = user.name.trim().split(/\s+/);
      if (nameParts.length > 0) {
        user.firstName = nameParts[0];
        user.lastName = nameParts.slice(1).join(' ');
      }
    }
    this.currentUserSubject.next(user);
  }

  getHomeRoute(): string {
    if (
      this.hasPermissionSync([
        Permissions.AccessRequestsListView4Customers,
        Permissions.AccessRequestsListView4Internals,
      ])
    ) {
      return '/requests';
    }

    if (
      this.hasPermissionSync([
        Permissions.AccessControlPanelUsersMenu,
        Permissions.AccessUserParentMenu,
      ])
    ) {
      return '/users';
    }

    if (this.hasPermissionSync([Permissions.AccessControlPanelCompaniesMenu])) {
      return '/company';
    }

    if (this.hasDiagnosticPagePermission()) {
      return '/diagnostics';
    }

    return '/unauthorized';
  }

  /**
   * Set authentication error message.
   * Used to display validation errors from BFF during login flow.
   */
  setAuthError(errorCode: string): void {
    const errorMessages: Record<string, string> = {
      NO_FPS_ROLE:
        'You do not have access to FPS. Please contact your administrator.',
      ACCOUNT_DEACTIVATED:
        'Your FPS access could not be verified and your account has been deactivated. Please contact your administrator.',
      USER_CREATION_FAILED:
        'Unable to create your FPS user account. Please try again later or contact your administrator.',
      USER_ACTIVATION_FAILED:
        'Unable to activate your FPS user account. Please try again later or contact your administrator.',
      USER_DEACTIVATION_FAILED:
        'Unable to update your FPS user access. Please try again later or contact your administrator.',
      ROLE_UPDATE_FAILED:
        'Unable to update your FPS role. Please try again later or contact your administrator.',
      SYNC_FAILED:
        'Unable to synchronize your FPS user account. Please try again later.',
      ACCOUNT_NOT_FOUND:
        'Your account was not found in the system. Please contact your administrator.',
      VALIDATION_FAILED:
        'Unable to validate user account. Please try again later.',
    };

    const message =
      errorMessages[errorCode] || 'Authentication failed. Please try again.';
    this.authErrorSubject.next(message);
  }

  /**
   * Clear authentication error message.
   */
  clearAuthError(): void {
    this.authErrorSubject.next(null);
  }

  /**
   * External login via Salesforce SAML.
   * Validates SAML response with BFF and creates session.
   * Returns user if successful, null if validation fails.
   */
  externalLogin(samlResponse: string): Observable<User | null> {
    return this.authRepository.externalLogin(samlResponse).pipe(
      map(response => {
        if (response.isAuthenticated && response.user) {
          this.setCurrentUser(response.user);
          this.permissionsSubject.next(response.permissions ?? []);
          return response.user;
        } else {
          this.currentUserSubject.next(null);
          this.permissionsSubject.next([]);
          return null;
        }
      }),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      catchError(_error => {
        this.currentUserSubject.next(null);
        this.permissionsSubject.next([]);
        return of(null);
      })
    );
  }
}
